/**
 * URL builders for the consumer Algolia search routes. Centralized here
 * so `@repo/ui` components (which can't depend on @repo/next-shared because
 * of the existing dep direction) and `apps/consumer` can share the same
 * encoding logic.
 *
 * Why `encodeURIComponent` and not `URLSearchParams` / `qs`:
 * `URLSearchParams` encodes spaces as `+` (form-encoded), while InstantSearch's
 * `simple` state mapping emits and reads `%20` (RFC 3986). If we emit `+`,
 * InstantSearch will re-normalize the URL on mount and push a duplicate
 * history entry — visible to the user as needing to press back twice to
 * leave the search results.
 *
 * Param ORDER also matters: InstantSearch writes the URL via `qs.stringify`,
 * which serializes the `routeState` keys in object order — `hierarchicalMenu`
 * (category) comes before `refinementList` (brand). If we emit them in a
 * different order, InstantSearch re-normalizes the URL on mount and pushes the
 * same duplicate history entry. Keep category before brand below.
 */

const BRAND_REFINEMENT_KEY = "products[refinementList][brand][0]";
const CATEGORY_KEY = "products[hierarchicalMenu][categoryIt.lvl0][0]";
const QUERY_KEY = "products[query]";

export function brandSearchUrl(brand: string | null | undefined): string {
  if (!brand) return "/search/results";
  return `/search/results?${encodeURIComponent(BRAND_REFINEMENT_KEY)}=${encodeURIComponent(brand)}`;
}

export function brandCategorySearchUrl(
  brand: string | null | undefined,
  category: string | null | undefined,
): string {
  if (!brand && !category) return "/search/results";
  const categorySearchUrl = `${encodeURIComponent(CATEGORY_KEY)}=${encodeURIComponent(category ?? "")}`;
  const brandSearchUrl = `${encodeURIComponent(BRAND_REFINEMENT_KEY)}=${encodeURIComponent(brand ?? "")}`;
  return `/search/results?${categorySearchUrl}&${brandSearchUrl}`;
}

export function searchQueryUrl(query: string | null | undefined): string {
  if (!query) return "/search/results";
  return `/search/results?${encodeURIComponent(QUERY_KEY)}=${encodeURIComponent(query)}`;
}
